/* eslint-disable camelcase */
/**
 * Adapters that map the new `stock-trading` order/history response schema back
 * to the legacy `stock-info` field names that the UI, selectors and reducers
 * still consume. Applying the mapping here (at the service boundary) keeps every
 * downstream component unchanged — the same approach as the pre-existing
 * `is_amend <- is_ammend` alias.
 *
 * New schema (list/history item): vendor_order_id, idx_order_id, order_num,
 *   status ("MATCHED"|"REJECTED"|"OPEN"...), type, side ("BUY"|"SELL"),
 *   stock_code, price, average_price, lot, executed_lot, total, is_amendable,
 *   is_cancelable, period ("GTC"|"DAY"), order_time, executed_fee, executed_total.
 * New schema (order detail): nested initial{price,lot,total,fee},
 *   executed{price,lot,total,fee}, stock{code,name}, reason, ...
 */

const SIDE_TO_TRX = { BUY: '1', SELL: '2' };

const PERIOD_MAP = { DAY: 'day', GTC: 'session', SESSION: 'session', SESI: 'session' };

// Legacy numeric status codes the UI branches on:
//   '0' = open (ModalOpenOrder filters status === '0')
//   '8' = rejected (Detail shows "Alasan" only when status === '8')
const STATUS_CODE = {
  OPEN: '0',
  QUEUED: '0',
  QUEUE: '0',
  PENDING: '0',
  SENT: '0',
  NEW: '0',
  WORKING: '0',
  PARTIAL: '0',
  PARTIAL_MATCHED: '0',
  PARTIALLY_MATCHED: '0',
  AMENDED: '0',
  AMEND: '0',
  MATCHED: '2',
  DONE: '2',
  FILLED: '2',
  FULLY_MATCHED: '2',
  FULL_MATCHED: '2',
  REJECTED: '8',
  REJECT: '8',
  CANCELLED: '9',
  CANCELED: '9',
  WITHDRAWN: '9',
  WITHDRAW: '9',
  EXPIRED: '9',
};

/** Return `value` unless it is null/undefined, in which case `fallback` (keeps 0). */
function pick(value, fallback) {
  return value === undefined || value === null ? fallback : value;
}

function titleCase(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/(^|_)([a-z])/g, (_match, sep, chr) => (sep ? ' ' : '') + chr.toUpperCase());
}

function statusToCode(status) {
  return STATUS_CODE[String(status || '').toUpperCase()] || '0';
}

function statusToLabel(status) {
  return titleCase(status);
}

function bool01(value) {
  return value ? '1' : '0';
}

function timeOnly(datetime) {
  const value = String(datetime || '');
  return value.includes('T') ? value.split('T')[1].slice(0, 8) : value;
}

function mapPeriod(period) {
  return PERIOD_MAP[String(period || '').toUpperCase()] || 'session';
}

/**
 * Open-order list item: new -> legacy. `order_no` is set to `vendor_order_id`
 * because the detail endpoint is now keyed by the vendor order id.
 */
export function mapOrderListItem(item = {}) {
  const lot = pick(item.lot, 0);
  const doneLot = pick(item.executed_lot, 0);

  return {
    ...item,
    order_no: pick(item.vendor_order_id, item.order_no),
    idx_order_no: pick(item.idx_order_id, item.idx_order_no),
    transaction_type: SIDE_TO_TRX[item.side] || item.transaction_type || '',
    stock: pick(item.stock_code, item.stock),
    price_order: pick(item.price, item.price_order),
    lot_input: lot,
    open_order: lot - doneLot,
    done_order: doneLot,
    total_price: pick(item.total, item.total_price),
    status: statusToCode(item.status),
    status_description: statusToLabel(item.status),
    is_ammend: bool01(item.is_amendable),
    is_amend: bool01(item.is_amendable),
    is_cancel: bool01(item.is_cancelable),
    period: mapPeriod(item.period),
    executed_time: timeOnly(item.updated_at),
  };
}

/** History list item: new -> legacy. */
export function mapHistoryListItem(item = {}) {
  return {
    ...item,
    order_no: pick(item.vendor_order_id, item.order_no),
    idx_order_no: pick(item.idx_order_id, item.idx_order_no),
    idx_trade_no: pick(item.idx_order_id, item.idx_trade_no),
    transaction_type: SIDE_TO_TRX[item.side] || item.transaction_type || '',
    stock: pick(item.stock_code, item.stock),
    lot: pick(item.lot, pick(item.executed_lot, 0)),
    price_matched: pick(item.average_price, pick(item.price, item.price_matched)),
    total_price: pick(item.executed_total, pick(item.total, item.total_price)),
    trade_date: pick(item.order_time, item.trade_date),
    fee: pick(item.executed_fee, pick(item.estimated_fee, pick(item.fee, null))),
    period: mapPeriod(item.period),
    status: statusToCode(item.status),
    status_description: statusToLabel(item.status),
    message: pick(item.reason, item.message),
  };
}

/** Order detail (nested new shape) -> flat legacy detail. */
export function mapOrderDetail(result) {
  if (!result) return result;

  const initial = result.initial || {};
  const executed = result.executed || {};
  const stock = result.stock || {};
  const initialLot = pick(initial.lot, 0);
  const doneLot = pick(executed.lot, 0);

  return {
    ...result,
    order_no: pick(result.vendor_order_id, result.order_no),
    idx_order_no: pick(result.idx_order_id, result.idx_order_no),
    transaction_type: SIDE_TO_TRX[result.side] || result.transaction_type || '',
    stock: pick(stock.code, result.stock),
    price_order: pick(executed.price, initial.price),
    lot_input: initialLot,
    open_order: initialLot - doneLot,
    done_order: doneLot,
    total_price: pick(executed.total, initial.total),
    status: statusToCode(result.status),
    status_description: statusToLabel(result.status),
    message: pick(result.reason, result.message),
    period: mapPeriod(result.period),
    is_ammend: bool01(result.is_amendable),
    is_amend: bool01(result.is_amendable),
    is_cancel: bool01(result.is_cancelable),
    executed_time: timeOnly(result.order_time),
    fee: pick(executed.fee, pick(initial.fee, null)),
  };
}

/**
 * Remap a list/history response envelope, exposing BOTH shapes the various
 * react-query hooks read:
 *   - `result.results` + `result.next`  (REG / MARGIN hooks)
 *   - flat `results` + `paging.next`     (ODT hooks)
 */
export function mapListEnvelope(data, mapItem) {
  const result = (data && data.result) || {};

  if (!Array.isArray(result.results)) {
    return data;
  }

  const results = result.results.map(mapItem);

  return {
    ...data,
    result: { ...result, results },
    results,
    paging: {
      count: result.count,
      next: result.next,
      previous: result.previous,
    },
  };
}

/**
 * Wrap a single mapped detail object into an envelope exposing both
 * `result.results[0]` and `results[0]`, matching every detail consumer.
 */
export function mapDetailEnvelope(data, mappedItem) {
  const result = (data && data.result) || {};

  return {
    ...data,
    result: { ...result, results: [mappedItem] },
    results: [mappedItem],
  };
}
